export type EnrichChatUserPart =
  | { type: "text"; text: string }
  | { type: "file"; url: string; mediaType: string; filename?: string; documentId?: string };

export type EnrichChatMessage = {
  id: string;
  role: "user" | "assistant";
  text: string;
  userParts?: EnrichChatUserPart[];
  skillsLoaded?: string[];
  /** False when parts were stored (A2). Heuristic must not run on these. */
  partsLegacy?: boolean;
};

export type EnrichPersistedChatDocument = {
  id: string;
  filename: string;
  mime_type: string | null;
  created_at: string;
};

export type EnrichPersistedChatMessage = {
  id: string;
  role: string;
  content: string;
  created_at?: string;
  parts_legacy?: boolean;
};

function ensureChatDocumentToolTagsInParts(parts: EnrichChatUserPart[]): EnrichChatUserPart[] {
  const hasTags = parts.some((p) => p.type === "text" && p.text.includes("[chat-document"));
  const fileParts = parts.filter(
    (p): p is Extract<EnrichChatUserPart, { type: "file" }> => p.type === "file" && Boolean(p.documentId),
  );
  if (fileParts.length === 0 || hasTags) return parts;
  const tagLines = fileParts.map(
    (p) =>
      `[chat-document documentId="${p.documentId}" filename="${p.filename || "dokument"}" mimeType="${p.mediaType}"]`,
  );
  return [
    ...parts,
    {
      type: "text" as const,
      text: `\n\nHochgeladene Dateien (für Tool-Aufrufe verwenden):\n${tagLines.join("\n")}`,
    },
  ];
}

/**
 * Attach chat-documents that have no [chat-document] tag in message content.
 * Only legacy rows (parts_legacy !== false) are eligible — persisted file parts
 * already carry documentId.
 */
export function enrichRestoredMessagesWithChatDocuments(
  messages: EnrichChatMessage[],
  rawMessages: EnrichPersistedChatMessage[],
  documents: EnrichPersistedChatDocument[],
): EnrichChatMessage[] {
  if (documents.length === 0) return messages;

  const usedDocIds = new Set<string>();
  for (const m of messages) {
    if (m.role !== "user" || !m.userParts) continue;
    for (const p of m.userParts) {
      if (p.type === "file" && p.documentId) usedDocIds.add(p.documentId);
    }
  }

  const userRaw = rawMessages
    .map((m, idx) => ({ m, idx }))
    .filter(({ m }) => m.role === "user" && m.created_at && m.parts_legacy !== false);

  const enriched = [...messages];
  for (const doc of documents) {
    if (usedDocIds.has(doc.id)) continue;

    const docTime = new Date(doc.created_at).getTime();
    let bestIdx = -1;
    let bestDelta = Infinity;
    for (const { m, idx } of userRaw) {
      const msgTime = new Date(m.created_at!).getTime();
      const delta = msgTime - docTime;
      if (delta >= 0 && delta < bestDelta) {
        bestDelta = delta;
        bestIdx = idx;
      }
    }
    if (bestIdx < 0 && userRaw.length > 0) {
      let closest = userRaw[0];
      let closestAbs = Math.abs(new Date(closest.m.created_at!).getTime() - docTime);
      for (const ur of userRaw) {
        const abs = Math.abs(new Date(ur.m.created_at!).getTime() - docTime);
        if (abs < closestAbs) {
          closestAbs = abs;
          closest = ur;
        }
      }
      bestIdx = closest.idx;
    }
    if (bestIdx < 0) continue;

    const msg = enriched[bestIdx];
    if (!msg || msg.role !== "user" || msg.partsLegacy === false) continue;

    const filePart: EnrichChatUserPart = {
      type: "file",
      url: "",
      mediaType: doc.mime_type || "application/octet-stream",
      filename: doc.filename,
      documentId: doc.id,
    };
    enriched[bestIdx] = {
      ...msg,
      userParts: ensureChatDocumentToolTagsInParts([...(msg.userParts ?? []), filePart]),
    };
    usedDocIds.add(doc.id);
  }

  return enriched;
}
